import { useId, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';

interface AuthFieldProps extends Omit<ComponentPropsWithoutRef<'input'>, 'className' | 'id'> {
  label: string;
  /** Rendered under the input, e.g. the live password rules. */
  hint?: ReactNode;
  /** Rendered right-aligned in the label row, e.g. a "Forgot password?" link. */
  trailing?: ReactNode;
}

/** Labelled 46px input; password fields get a Show/Hide button inside the input. */
export function AuthField({ label, hint, trailing, type, ...inputProps }: AuthFieldProps) {
  const id = useId();
  const isPassword = type === 'password';
  const [isRevealed, setIsRevealed] = useState(false);

  return (
    <div className="afield">
      <div className="afield__labelrow">
        <label className="afield__label" htmlFor={id}>
          {label}
        </label>
        {trailing ? <span className="afield__trailing">{trailing}</span> : null}
      </div>
      <div className="afield__box">
        <input
          id={id}
          className={isPassword ? 'afield__input afield__input--reveal' : 'afield__input'}
          type={isPassword && isRevealed ? 'text' : type}
          {...inputProps}
        />
        {isPassword ? (
          <button
            type="button"
            className="afield__reveal"
            aria-label={isRevealed ? 'Hide password' : 'Show password'}
            onClick={() => setIsRevealed((value) => !value)}
          >
            {isRevealed ? 'Hide' : 'Show'}
          </button>
        ) : null}
      </div>
      {hint}
    </div>
  );
}

/** The minimum the screens promise; Supabase holds the enforced minimum server-side. */
const MIN_PASSWORD_LENGTH = 8;

/** The live rule row under a new-password field: ticks green once it is met. */
export function PasswordRules({ password }: { password: string }) {
  const isMet = password.length >= MIN_PASSWORD_LENGTH;
  return (
    <ul className="afield__rules" aria-label="Password rules">
      <li className={isMet ? 'afield__rule afield__rule--met' : 'afield__rule'}>
        <span className="afield__rule-mark" aria-hidden="true">
          {isMet ? '✓' : ''}
        </span>
        {MIN_PASSWORD_LENGTH}+ characters
      </li>
    </ul>
  );
}

/** The red alert row above the submit. */
export function AuthAlert({ message }: { message: string }) {
  return (
    <p className="auth-alert" role="alert">
      <span className="auth-alert__mark" aria-hidden="true">
        !
      </span>
      {message}
    </p>
  );
}

/** The full-width dark submit; a spinner and "One moment…" while busy. */
export function AuthSubmit({ label, isBusy }: { label: string; isBusy: boolean }) {
  return (
    <button className="auth-submit" type="submit" disabled={isBusy}>
      {isBusy ? <span className="auth-submit__spinner" aria-hidden="true" /> : null}
      {isBusy ? 'One moment…' : label}
    </button>
  );
}
